// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//       • Withdraw or disable access to the application or related services
//       • Remove hosted, cloud-based, or linked services under his control
//       • Revoke access to Build My Checks–related assets, code, APIs, or integrations
//
//  © 2025 Gareth Morgan — BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks – ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/Navbar';
import BuildMyChecksSocialLinks from '../../components/BuildMyChecksSocialLinks';

export default function Features() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isIOS } = (() => {
    if (typeof window === 'undefined') return { isIOS: false, isAndroid: false };
    const params = new URLSearchParams(window.location.search || '');
    const forcedIOS = params.get('platform') === 'ios';
    const forcedAndroid = params.get('platform') === 'android';
    const ua = (window.navigator?.userAgent || '').toLowerCase();
    const platform = (window.navigator?.platform || '').toLowerCase();
    const touchCapable = typeof window.navigator?.maxTouchPoints === 'number' && window.navigator.maxTouchPoints > 1;
    const isIOSDetected =
      forcedIOS ||
      /iphone|ipad|ipod|safariviewservice/.test(ua) ||
      ((platform === 'macintel' || ua.includes('mac os x')) && touchCapable);
    const isAndroidDetected = forcedAndroid || /android/.test(ua);
    return { isIOS: isIOSDetected, isAndroid: isAndroidDetected };
  })();

  useEffect(() => {
    const previousTitle = document.title;
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    const previousDescription = metaDescription.getAttribute('content');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute(
      'content',
      'Explore BuildMyChecks features including AI document to checklist conversion, digital inspections, split workflow forms, policies and procedures, AI policy search, Knowledge Hub compliance records, training sign-off and audit-ready reporting.'
    );
    if (!metaDescription.parentNode) document.head.appendChild(metaDescription);
    document.title = 'BuildMyChecks Features | AI Document to Checklist Builder, Knowledge Hub & Digital Inspection Software';

    return () => {
      document.title = previousTitle;
      if (previousDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-red-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-extrabold mb-6">{t('subscription.feature.heroTitle', 'Powerful Features for Digital Inspections')}</h1>
          <p className="text-xl max-w-3xl mx-auto">
            {t('subscription.feature.heroSubtitle', 'Everything you need to create custom checklists, conduct inspections, and manage quality control - all in one powerful platform')}
          </p>
        </div>
      </section>

      {/* AI Knowledge Hub SEO Feature */}
      <section className="bg-slate-950 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <div>
              <p className="uppercase tracking-[0.22em] text-cyan-300 text-sm font-semibold mb-4">
                {t('subscription.knowledgeHubMarketing.features.eyebrow', 'AI-powered compliance knowledge hub')}
              </p>
              <h2 className="text-4xl font-extrabold mb-5">
                {t('subscription.knowledgeHubMarketing.features.title', 'Search policies, procedures and training records with AI assistance')}
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                {t('subscription.knowledgeHubMarketing.features.description', 'BuildMyChecks now brings controlled documents, staff acknowledgements, assessment results and audit evidence into one searchable Knowledge Hub. Teams can ask natural language questions and get answers from generated policy summaries without rescanning large documents every time.')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.knowledgeHubMarketing.features.searchTitle', 'AI policy search')}</h3>
                  <p className="text-slate-300">{t('subscription.knowledgeHubMarketing.features.searchText', 'Ask questions such as "What PPE is required?" and receive source-linked answers from approved policy summaries.')}</p>
                </div>
                <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.knowledgeHubMarketing.features.suggestionsTitle', 'Role-based suggestions')}</h3>
                  <p className="text-slate-300">{t('subscription.knowledgeHubMarketing.features.suggestionsText', 'Show relevant documents based on user role, group, department and outstanding sign-off requirements.')}</p>
                </div>
              </div>
            </div>
            <div className="rounded-3xl bg-white text-slate-950 p-8 shadow-2xl">
              <h3 className="text-2xl font-extrabold mb-4">{t('subscription.knowledgeHubMarketing.features.complianceTitle', 'Knowledge Hub compliance features')}</h3>
              <ul className="space-y-4 text-gray-700">
                <li><strong>{t('subscription.knowledgeHubMarketing.features.indexLabel', 'Controlled document index:')}</strong> {t('subscription.knowledgeHubMarketing.features.indexText', 'manage policy versions, review dates, expiry dates and revision history.')}</li>
                <li><strong>{t('subscription.knowledgeHubMarketing.features.recordsLabel', 'Training record dashboard:')}</strong> {t('subscription.knowledgeHubMarketing.features.recordsText', 'view completed, pending, overdue and failed policy sign-offs.')}</li>
                <li><strong>{t('subscription.knowledgeHubMarketing.features.evidenceLabel', 'Assessment evidence:')}</strong> {t('subscription.knowledgeHubMarketing.features.evidenceText', 'track user answers, pass percentages, signatures, timestamps, device and IP metadata.')}</li>
                <li><strong>{t('subscription.knowledgeHubMarketing.features.exportsLabel', 'Audit-ready exports:')}</strong> {t('subscription.knowledgeHubMarketing.features.exportsText', 'export policy training records for inspections, accreditations and compliance audits.')}</li>
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="mt-6 w-full bg-primary hover:bg-red-700 text-white px-6 py-3 rounded-xl font-bold"
              >
                {t('subscription.knowledgeHubMarketing.features.cta', 'Start building your compliance knowledge hub')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* AI Document Import SEO Feature */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-10 items-center">
            <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white p-8 shadow-2xl">
              <p className="uppercase tracking-[0.22em] text-cyan-300 text-sm font-semibold mb-4">
                {t('subscription.feature.aiImport.eyebrow', 'New AI document to checklist upgrade')}
              </p>
              <h2 className="text-4xl font-extrabold mb-5">
                {t('subscription.feature.aiImport.title', 'Upload a document and turn it into a digital inspection draft')}
              </h2>
              <p className="text-lg text-slate-300 mb-6">
                {t('subscription.feature.aiImport.description', 'BuildMyChecks now helps teams upload PDFs, Word documents, text files and existing procedures, then convert them into editable draft checklists using AI. Review suggested sections, field types, evidence prompts, workflow stages and scoring before sending the draft into the full form builder.')}
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.feature.aiImport.reviewTitle', 'Review before publish')}</h3>
                  <p className="text-slate-300">{t('subscription.feature.aiImport.reviewText', 'Edit AI-generated titles, sections, fields, option scores and evidence requirements before the form builder opens.')}</p>
                </div>
                <div className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <h3 className="font-bold text-xl mb-2">{t('subscription.feature.aiImport.presetTitle', 'Company preset library')}</h3>
                  <p className="text-slate-300">{t('subscription.feature.aiImport.presetText', 'Save reusable import presets by industry or process with starter sections, field skeletons and extra drafting instructions.')}</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-8 shadow-xl">
              <h3 className="text-2xl font-extrabold mb-4 text-slate-950">{t('subscription.feature.aiImport.bulletsTitle', 'AI checklist builder features')}</h3>
              <ul className="space-y-4 text-gray-700">
                <li><strong>{t('subscription.feature.aiImport.extractLabel', 'Document upload support:')}</strong> {t('subscription.feature.aiImport.extractText', 'convert PDF, DOCX, TXT and JSON sources into editable checklist drafts.')}</li>
                <li><strong>{t('subscription.feature.aiImport.smartFieldsLabel', 'Smart field detection:')}</strong> {t('subscription.feature.aiImport.smartFieldsText', 'suggest yes/no, text, number, date, media, signature, location and multiple choice fields automatically.')}</li>
                <li><strong>{t('subscription.feature.aiImport.workflowLabel', 'Workflow-aware drafting:')}</strong> {t('subscription.feature.aiImport.workflowText', 'spot repeatable sections, staged handoffs, assignment hints and evidence follow-ups for more advanced inspections.')}</li>
                <li><strong>{t('subscription.feature.aiImport.reorderLabel', 'Pre-builder editing:')}</strong> {t('subscription.feature.aiImport.reorderText', 'add, remove and drag-to-reorder sections or fields before opening the full drag-and-drop form builder.')}</li>
                <li><strong>{t('subscription.feature.aiImport.presetLibraryLabel', 'Reusable company presets:')}</strong> {t('subscription.feature.aiImport.presetLibraryText', 'create named import playbooks such as kitchen audits, vehicle checks or weekly cleaning rounds.')}</li>
              </ul>
              <button
                onClick={() => navigate('/register')}
                className="mt-6 w-full bg-primary hover:bg-red-700 text-white px-6 py-3 rounded-xl font-bold"
              >
                {t('subscription.feature.aiImport.cta', 'Start building AI-generated inspections')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('subscription.feature.sectionTitle', 'Complete Digital Inspection Solution')}</h2>
          <p className="text-xl text-gray-600">{t('subscription.feature.sectionSubtitle', 'Built for businesses that need reliable, easy-to-use inspection software')}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12">
          {/* Form Builder */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">🛠️</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.formBuilder.title', 'Advanced Form Builder')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.formBuilder.fieldTypesLabel', '10+ Field Types:')}</strong> {t('subscription.feature.formBuilder.fieldTypesDesc', 'Text, numbers, dates, photos, signatures, checkboxes, dropdowns, location, and more')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.formBuilder.dragDropLabel', 'Drag & Drop Builder:')}</strong> {t('subscription.feature.formBuilder.dragDropDesc', 'Create forms in minutes with intuitive interface')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.formBuilder.conditionalLabel', 'Conditional Logic:')}</strong> {t('subscription.feature.formBuilder.conditionalDesc', 'Show/hide fields based on previous answers')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.formBuilder.validationLabel', 'Validation Rules:')}</strong> {t('subscription.feature.formBuilder.validationDesc', 'Ensure data quality with required fields and format checks')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.formBuilder.templatesLabel', 'Templates:')}</strong> {t('subscription.feature.formBuilder.templatesDesc', 'Pre-built forms for common inspections or create your own')}</span>
              </li>
            </ul>
          </div>

          {/* Mobile App */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">📱</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.mobile.title', 'Mobile-First Design')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.mobile.nativeLabel', 'Native Mobile App:')}</strong> {t('subscription.feature.mobile.nativeDesc', 'Optimized for iOS and Android devices')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.mobile.offlineLabel', 'Offline Mode:')}</strong> {t('subscription.feature.mobile.offlineDesc', 'Conduct inspections without internet, sync when online')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.mobile.cameraLabel', 'Camera Integration:')}</strong> {t('subscription.feature.mobile.cameraDesc', 'Capture photos directly from the app')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.mobile.gpsLabel', 'GPS Location:')}</strong> {t('subscription.feature.mobile.gpsDesc', 'Automatic location tracking for site inspections')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.mobile.touchLabel', 'Touch-Optimized:')}</strong> {t('subscription.feature.mobile.touchDesc', 'Large buttons and gestures for field use')}</span>
              </li>
            </ul>
          </div>

          {/* Draft System */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">💾</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.draft.title', 'Smart Draft System')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.draft.autoSaveLabel', 'Auto-Save Progress:')}</strong> {t('subscription.feature.draft.autoSaveDesc', 'Never lose your work - drafts save automatically')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.draft.resumeLabel', 'Resume Anytime:')}</strong> {t('subscription.feature.draft.resumeDesc', 'Pick up where you left off on any device')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.draft.multipleLabel', 'Multiple Drafts:')}</strong> {t('subscription.feature.draft.multipleDesc', 'Work on multiple inspections simultaneously')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.draft.dashboardLabel', 'Draft Dashboard:')}</strong> {t('subscription.feature.draft.dashboardDesc', 'See all incomplete inspections at a glance')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.draft.lastSavedLabel', 'Last Saved Info:')}</strong> {t('subscription.feature.draft.lastSavedDesc', 'Know exactly when you last updated each draft')}</span>
              </li>
            </ul>
          </div>

          {/* User Management */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">👥</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.team.title', 'Team Management')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.team.unlimitedLabel', 'Unlimited Users:')}</strong> {t('subscription.feature.team.unlimitedDesc', 'Add as many team members as you need (£5/user/month)')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.team.rolesLabel', 'Role-Based Access:')}</strong> {t('subscription.feature.team.rolesDesc', 'Admin, Super User, and Standard User roles')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.team.groupsLabel', 'Group Management:')}</strong> {t('subscription.feature.team.groupsDesc', 'Organize users by department, site, or team')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.team.permissionsLabel', 'Permission Control:')}</strong> {t('subscription.feature.team.permissionsDesc', 'Restrict form access to specific users or groups')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.team.analyticsLabel', 'User Analytics:')}</strong> {t('subscription.feature.team.analyticsDesc', "Track who's completing inspections and when")}</span>
              </li>
            </ul>
          </div>

          {/* Approval Workflows */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">✅</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.approval.title', 'Approval Workflows')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.approval.multiStageLabel', 'Multi-Stage Approval:')}</strong> {t('subscription.feature.approval.multiStageDesc', 'Set up complex approval chains')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.approval.roleBasedLabel', 'Role-Based Approvers:')}</strong> {t('subscription.feature.approval.roleBasedDesc', 'Assign approvals by user role or specific users')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.approval.commentsLabel', 'Approval Comments:')}</strong> {t('subscription.feature.approval.commentsDesc', 'Add notes when approving or rejecting')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.approval.statusLabel', 'Status Tracking:')}</strong> {t('subscription.feature.approval.statusDesc', 'See approval progress in real-time')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.approval.emailLabel', 'Email Notifications:')}</strong> {t('subscription.feature.approval.emailDesc', 'Automatic alerts for pending approvals')}</span>
              </li>
            </ul>
          </div>

          {/* Scheduling */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">📅</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.scheduling.title', 'Smart Scheduling')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.scheduling.recurringLabel', 'Recurring Inspections:')}</strong> {t('subscription.feature.scheduling.recurringDesc', 'Set daily, weekly, or monthly schedules')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.scheduling.assignLabel', 'Assign to Users:')}</strong> {t('subscription.feature.scheduling.assignDesc', 'Automatically assign scheduled checks to team members')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.scheduling.remindersLabel', 'Reminders:')}</strong> {t('subscription.feature.scheduling.remindersDesc', 'Push notifications for upcoming inspections')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.scheduling.calendarLabel', 'Calendar View:')}</strong> {t('subscription.feature.scheduling.calendarDesc', 'See all scheduled inspections at a glance')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.scheduling.complianceLabel', 'Compliance Tracking:')}</strong> {t('subscription.feature.scheduling.complianceDesc', 'Never miss a required inspection')}</span>
              </li>
            </ul>
          </div>

          {/* Reporting */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.reports.title', 'Reports & Analytics')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.reports.pdfLabel', 'PDF Export:')}</strong> {t('subscription.feature.reports.pdfDesc', 'Professional reports with photos and signatures')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.reports.csvLabel', 'CSV Export:')}</strong> {t('subscription.feature.reports.csvDesc', 'Download data for Excel analysis')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.reports.dashboardLabel', 'Real-Time Dashboard:')}</strong> {t('subscription.feature.reports.dashboardDesc', 'Live view of completed inspections')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.reports.filtersLabel', 'Custom Filters:')}</strong> {t('subscription.feature.reports.filtersDesc', 'Filter by date, user, location, or status')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.reports.trendLabel', 'Trend Analysis:')}</strong> {t('subscription.feature.reports.trendDesc', 'Identify patterns and compliance issues')}</span>
              </li>
            </ul>
          </div>

          {/* Media & Attachments */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">📷</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.media.title', 'Media & File Management')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.media.photoLabel', 'Photo Capture:')}</strong> {t('subscription.feature.media.photoDesc', 'Take photos with device camera or upload from gallery')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.media.signaturesLabel', 'Digital Signatures:')}</strong> {t('subscription.feature.media.signaturesDesc', 'Collect signatures with touch or stylus')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.media.attachmentsLabel', 'File Attachments:')}</strong> {t('subscription.feature.media.attachmentsDesc', 'Attach PDFs, documents, and images')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.media.storageLabel', 'Cloud Storage:')}</strong> {t('subscription.feature.media.storageDesc', 'Secure storage with automatic backup')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.media.compressionLabel', 'Image Compression:')}</strong> {t('subscription.feature.media.compressionDesc', 'Optimized storage without quality loss')}</span>
              </li>
            </ul>
          </div>

          {/* Security */}
          <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
            <div className="text-5xl mb-4">🔒</div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('subscription.feature.security.title', 'Enterprise Security')}</h3>
            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.security.encryptionLabel', 'Data Encryption:')}</strong> {t('subscription.feature.security.encryptionDesc', 'End-to-end encryption for all data')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.security.gdprLabel', 'GDPR Compliant:')}</strong> {t('subscription.feature.security.gdprDesc', 'Full compliance with UK data protection laws')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.security.auditLabel', 'Audit Trails:')}</strong> {t('subscription.feature.security.auditDesc', 'Complete history of all changes and actions')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.security.authLabel', 'Secure Authentication:')}</strong> {t('subscription.feature.security.authDesc', 'Industry-standard Firebase security')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary mr-2">✓</span>
                <span><strong>{t('subscription.feature.security.backupsLabel', 'Regular Backups:')}</strong> {t('subscription.feature.security.backupsDesc', 'Automatic daily backups of all data')}</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* New Features Highlight */}
      <section className="bg-gradient-to-br from-gray-100 to-gray-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('subscription.feature.recentTitle', 'Recently Added Features')}</h2>
            <p className="text-xl text-gray-600">{t('subscription.feature.recentSubtitle', 'We\'re constantly improving Build My Checks with new capabilities')}</p>
          </div>

          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">AI</span>
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.feature.aiImport.cardTitle', 'AI Document to Checklist')}</h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t('subscription.feature.aiImport.cardDesc', 'Upload policies, SOPs, audit sheets or existing paperwork and let AI turn them into editable digital inspection drafts for your team.')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.aiImport.cardPoint1', 'Generate sections, field types, scoring and evidence prompts from uploaded documents')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.aiImport.cardPoint2', 'Review and edit the AI draft before opening the full form builder')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.aiImport.cardPoint3', 'Use company preset libraries to standardise imports by site, team or industry')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">AI</span>
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.knowledgeHubMarketing.card.title', 'AI Knowledge Hub')}</h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t('subscription.knowledgeHubMarketing.card.description', 'Search policies and procedures using natural language, ask questions about controlled documents, and get source-linked answers from generated policy summaries.')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.knowledgeHubMarketing.card.point1', 'AI-powered policy search without rescanning large PDFs for every question')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.knowledgeHubMarketing.card.point2', 'Suggested documents based on role, group, pending sign-offs and available summaries')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.knowledgeHubMarketing.card.point3', 'Answers include source policy titles and versions for audit confidence')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">WF</span>
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.feature.splitWorkflowHighlight.title', 'Split Workflow Inspections')}</h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t('subscription.feature.splitWorkflowHighlight.desc', 'Build advanced digital inspection workflows by splitting one inspection form into assigned sections for admins, users, teams, or departments. Each stage is completed in order, making multi-user inspections easier to control across web and mobile.')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.splitWorkflowHighlight.assignedSections', 'Assign inspection sections to specific users or groups')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.splitWorkflowHighlight.sequentialAccess', 'Only the active workflow step can start or continue the check')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.splitWorkflowHighlight.auditVisibility', 'Control previous-section visibility with a full audit trail of edits')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">💾</span>
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.feature.draftHighlight.title', 'Draft System')}</h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t('subscription.feature.draftHighlight.desc', 'Never lose your progress again! Our new draft system automatically saves your work as you go. Start an inspection on mobile, continue on desktop, and finish later - all your data is preserved.')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{t('subscription.feature.draftHighlight.autoSave', 'Auto-save every change')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{t('subscription.feature.draftHighlight.resume', 'Resume on any device')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{t('subscription.feature.draftHighlight.lastSaved', 'See when you last saved')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">👥</span>
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.feature.userMgmtHighlight.title', 'Enhanced User Management')}</h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t('subscription.feature.userMgmtHighlight.desc', 'Powerful new user management tools now available on both web and mobile. Admins can add, edit, and manage team members from anywhere, with full control over roles and permissions.')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{t('subscription.feature.userMgmtHighlight.mobileManagement', 'Mobile user management')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{t('subscription.feature.userMgmtHighlight.advancedRoles', 'Advanced role controls')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2">✓</span>
                  <span>{t('subscription.feature.userMgmtHighlight.groupAssign', 'Group assignments')}</span>
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <div className="flex items-center mb-4">
                <span className="text-3xl mr-3">MDL</span>
                <h3 className="text-2xl font-bold text-gray-900">{t('subscription.feature.documentControlHighlight.title', 'Document Control Index')}</h3>
              </div>
              <p className="text-gray-600 mb-4">
                {t('subscription.feature.documentControlHighlight.desc', 'Track every inspection through a master document list with revision control, approval metadata, and a full audit trail across web and mobile.')}
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.documentControlHighlight.masterList', 'Central master document list with current revision and approval details')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.documentControlHighlight.revisionHistory', 'Full revision history with who changed what and when')}</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2" aria-hidden="true">&#10003;</span>
                  <span>{t('subscription.feature.documentControlHighlight.mobileLimit', 'Clear web-only versus mobile-editable inspection status')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">{t('subscription.feature.ctaTitle', 'Ready to Transform Your Inspections?')}</h2>
          <p className="text-xl mb-8">
            {t('subscription.feature.ctaSubtitle', 'Join hundreds of businesses using Build My Checks for digital inspections and quality control')}
          </p>
          <button
            onClick={() => navigate('/register')}
            className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-semibold shadow-lg"
          >
            {t('subscription.feature.ctaButton', 'Start Free Trial - No Credit Card Required')}
          </button>
          <p className="text-sm mt-4 opacity-90">{t('subscription.pricePerUser', '£5 per user per month • Cancel anytime')}</p>
        </div>
      </section>

      {/* Mobile App Download Section */}
      <section className="bg-gradient-to-r from-primary to-red-700 py-16 mb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">{t('subscription.feature.mobileTitle', 'Take Your Inspections Mobile')}</h2>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
            {t('subscription.feature.mobileSubtitle', 'Download our mobile app for iOS and Android. Conduct checks anywhere, even offline.')}
          </p>
          <div className="flex justify-center items-center gap-6 flex-wrap">
            <a 
              href="https://apps.apple.com/app/id6758455875" 
              className="block transition-transform hover:scale-105"
              aria-label="Download on the App Store"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img 
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&amp;releaseDate=1234567890" 
                alt="Download on the App Store" 
                className="h-14"
              />
            </a>
            {!isIOS && (
              <a
                href="https://play.google.com/store/apps/details?id=com.buildmychecks.mobile"
                className="block transition-transform hover:scale-105"
                aria-label="Get it on Google Play"
              >
                <img
                  src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                  alt="Get it on Google Play"
                  className="h-[72px]"
                />
              </a>
            )}
          </div>
          <p className="text-sm text-white mt-4 opacity-90">
            {isIOS ? t('subscription.feature.availableAppStore', 'Available on the App Store') : t('subscription.feature.availableBoth', 'Available on the App Store and Google Play')}
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-bold mb-4">{t('footer.brandName', 'Build My Checks')}</h3>
              <p className="text-gray-400 text-sm">{t('footer.tagline', 'Digital checklist and inspection software for businesses.')}</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.product', 'Product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="text-gray-400 hover:text-white">{t('nav.features', 'Features')}</button></li>
                <li><button onClick={() => navigate('/')} className="text-gray-400 hover:text-white">{t('nav.pricing', 'Pricing')}</button></li>
                <li><button onClick={() => navigate('/register')} className="text-gray-400 hover:text-white">{t('subscription.feature.startFreeTrial', 'Start Free Trial')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('footer.company', 'Company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white">{t('nav.contact', 'Contact')}</button></li>
                <li><button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-white">{t('footer.privacyPolicy', 'Privacy Policy')}</button></li>
                <li><button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-white">{t('footer.termsOfService', 'Terms of Service')}</button></li>
                <li><button onClick={() => navigate('/refund-policy')} className="text-gray-400 hover:text-white">{t('footer.refundPolicy', 'Refund Policy')}</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">{t('nav.contact', 'Contact')}</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>support@buildmychecks.co.uk</li>
                <li>sales@buildmychecks.co.uk</li>
                <li className="pt-4">
                  <BuildMyChecksSocialLinks />
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 {t('footer.brandName', 'Build My Checks')}. {t('footer.allRightsReserved', 'All rights reserved.')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------

